import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router';
import { getCardOptimizerRoutes } from 'routes/card-optimizer/routes';
import { getDisruptionMonitorRoutes } from 'routes/disruption-monitor/routes';
import { getRecallsRoutes } from 'routes/recalls/routes';
import { getGlobalResourceRoutes } from 'routes/resources/routes';
import { getTaskManagerRoutes } from 'routes/task-manager/routes';

import { GLOBAL_IMAGES_ACTION_PATH } from 'pages/global-images/constants';
import { RESILIENCY_MONITOR_BASE_ROUTE } from 'pages/resiliency-monitor/base-path';

export function getRouter(args: {
  hasCardOptimizerV2: boolean;
  hasCardOptimizerDashboardV2: boolean;
  hasDisruptionsV2: boolean;
  hasRecallsV2: boolean;
  hasResourcesV2: boolean;
  hasTaskManagerV2Routes: boolean;
}) {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route id="root" path="/" lazy={() => import('Root')}>
        {getGlobalResourceRoutes(args.hasResourcesV2)}

        {/* Redirects */}
        <Route path="/" lazy={() => import('components/RouteContainers/LandingRedirect')} />
        {/* Landing Page */}
        <Route path="/home" lazy={() => import('pages/home')} />
        {/* Auth / Profile */}
        <Route path="/reset-password" lazy={() => import('pages/forgot-password/routes/reset-password')}>
          <Route index lazy={() => import('pages/forgot-password/routes/reset-password.index')} />
          <Route path="confirm" lazy={() => import('pages/forgot-password/routes/reset-password.confirm')} />
          <Route path="success" lazy={() => import('pages/forgot-password/routes/reset-password.success')} />
        </Route>

        <Route path="/login" lazy={() => import('pages/login/routes/login')}>
          <Route index lazy={() => import('pages/login/routes/login.index')} />
          <Route path="sso" lazy={() => import('pages/login/routes/login.sso')} />
          <Route path="callback" lazy={() => import('pages/login/routes/login.callback')} />
        </Route>
        <Route path="/logout" lazy={() => import('pages/logout')} />
        <Route path="/profile" lazy={() => import('pages/profile')} />
        <Route path="/spoof" lazy={() => import('pages/spoofing')} />
        <Route path="/admin" id="admin-root" lazy={() => import('pages/admin/routes/admin')}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" lazy={() => import('pages/admin/routes/admin.users')}>
            <Route path="new" lazy={() => import('pages/admin/routes/admin.users.new')} />
            <Route path=":userId" id="admin-userId" lazy={() => import('pages/admin/routes/admin.users.$userId')}>
              <Route index lazy={() => import('pages/admin/routes/admin.users.$userId.user')} />
              <Route path="access" lazy={() => import('pages/admin/routes/admin.users.$userId.access')} />
              <Route path="deactivate" lazy={() => import('pages/admin/routes/admin.users.$userId.deactivate')} />
            </Route>
          </Route>

          <Route path="teams" lazy={() => import('pages/admin/routes/admin.teams')}>
            <Route path="new" lazy={() => import('pages/admin/routes/admin.teams.new')} />
            <Route path=":teamId" id="admin-teamId" lazy={() => import('pages/admin/routes/admin.teams.$teamId')}>
              <Route path="delete" lazy={() => import('pages/admin/routes/admin.teams.$teamId.delete')} />
            </Route>
          </Route>
          <Route
            path="configuration-manager"
            lazy={() => import('pages/admin/routes/configuration-manager/configuration-manager-layout.route')}
          >
            <Route
              index
              lazy={() => import('pages/admin/routes/configuration-manager/configuration-manager-index.route')}
            />
            <Route
              path="custom-task-statuses"
              lazy={() => import('pages/admin/routes/custom-task-statuses/custom-task-statuses.route')}
            />
            <Route
              path="custom-task-statuses/:taskTypeId"
              lazy={() => import('pages/admin/routes/custom-task-statuses/custom-task-statuses-task-type.route')}
            />
          </Route>
        </Route>
        <Route path="exceptions" lazy={() => import('pages/exceptions/routes/exceptions')}>
          <Route index lazy={() => import('pages/exceptions/routes/exceptions.index')} />
          <Route path=":poId" lazy={() => import('pages/exceptions/routes/exceptions.$poId')} />
          <Route path="dashboard" lazy={() => import('pages/exceptions/routes/exceptions.dashboard')} />
        </Route>

        <>
          {/* 
          Disruption Monitor Routes
          
          will someday be replaced with: 
          {...prefix('/disruptions', disruptionMonitorRoutes)}
          */}
          {getDisruptionMonitorRoutes(args.hasDisruptionsV2)}

          {getCardOptimizerRoutes(args.hasCardOptimizerV2, args.hasCardOptimizerDashboardV2)}

          {getRecallsRoutes(args.hasRecallsV2)}
        </>

        {/* TODO V2 External Preference Cards Approvals */}
        <Route
          path="external/preference-cards/approvals/:requestApprovalId"
          lazy={() =>
            import('pages/external/preference-cards/routes/external.preference-cards.approvals.$requestApprovalId')
          }
        />

        <Route path="substitutes" lazy={() => import('pages/substitutes/routes/substitutes')}>
          <Route index lazy={() => import('pages/substitutes/routes/substitutes.index')} />
          <Route
            path=":substituteId"
            id="substitute_id"
            lazy={() => import('pages/substitutes/routes/substitutes.$substituteId')}
          >
            <Route
              path=""
              id="substitute_id_index"
              lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.index')}
            >
              <Route path="request" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.request')} />
              <Route path="edit" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.edit')} />
              <Route
                path="approval"
                lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.approval')}
              />
            </Route>
            <Route path="tasks" lazy={() => import('pages/substitutes/routes/substitutes.$substituteId.tasks')} />
          </Route>
          <Route path="overview" lazy={() => import('pages/substitutes/routes/substitutes.overview')} />
        </Route>
        <Route path="/create-substitute-request" lazy={() => import('pages/root/create-substitute-request')} />
        <Route
          path="external/substitutes/approvals/:requestId"
          lazy={() => import('pages/external/substitutes/approvals.route')}
        />
        <Route
          path={RESILIENCY_MONITOR_BASE_ROUTE}
          lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor')}
        >
          <Route index lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor.index')} />
          <Route
            path="categories"
            lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor.categories')}
          />
          <Route path="contracts" lazy={() => import('pages/resiliency-monitor/routes/resiliency-monitor.contracts')} />
        </Route>
        {getTaskManagerRoutes(args.hasTaskManagerV2Routes)}
        <Route path={GLOBAL_IMAGES_ACTION_PATH} lazy={() => import('pages/global-images/routes/global-images')} />
        <Route path="*" lazy={() => import('pages/not-found')} />
        <Route path="oculus" lazy={() => import('pages/oculus/routes/oculus')}>
          <Route index lazy={() => import('pages/oculus/routes/oculus.index')} />
          <Route path=":threadId" lazy={() => import('pages/oculus/routes/oculus.$threadId')} />
        </Route>
        <Route path="/resources/tasks" lazy={() => import('pages/resources/tasks/tasks.route')} />
        <Route path="/resources/activity-thread/:threadId" lazy={() => import('routes/resources/activity-thread')} />
      </Route>
    )
  );
}
